import { Injectable } from '@angular/core';
import { Directory, Filesystem, ReadFileResult, WriteFileResult } from '@capacitor/filesystem';
import { DebugService } from './debug.service';

export const CACHE_FOLDERS = { DOCUMENTS: 'documents', NEWS: 'news', E_DOCUMENTS: 'eDocuments', CHAT: 'chat' } as const;

@Injectable({
  providedIn: 'root'
})
export class FileCacheService {
  private readonly cacheDirectory = Directory.Cache;
  private readonly downloadedIconIds = new Set<string>();

  constructor(private readonly debugService: DebugService) {}

  async isFileInCache(fileName: string, path: string): Promise<boolean> {
    try {
      const result = await Filesystem.stat({
        path: this.concatenatePath(path, fileName),
        directory: this.cacheDirectory
      });
      // warning: if this condition returns false, it means there is a directory with that name in the cache directory
      return result.type === 'file';
    } catch (error) {
      // if the file does not exist, Filesystem.stat() fails with an error
      this.debugService.logMessage(error.message);
      return false;
    }
  }

  async createCacheDirectory(path: string): Promise<string> {
    await Filesystem.mkdir({
      path,
      directory: this.cacheDirectory,
      recursive: true
    });

    return this.getCacheDirectory(path);
  }

  async getCacheDirectory(path: string): Promise<string> {
    const result = await Filesystem.stat({
      path,
      directory: this.cacheDirectory
    });

    if (result.type === 'directory') {
      return result.uri;
    }
    return Promise.reject(`File at path: '${path}' is not a directory`);
  }

  writeFile(fileName: string, path: string, data: string): Promise<WriteFileResult> {
    return Filesystem.writeFile({
      path: this.concatenatePath(path, fileName),
      data,
      directory: this.cacheDirectory,
      recursive: true
    });
  }

  readFile(fileName: string, path: string): Promise<ReadFileResult> {
    return Filesystem.readFile({
      path: this.concatenatePath(path, fileName),
      directory: this.cacheDirectory
    });
  }

  deleteFile(fileName: string, path: string): Promise<void> {
    return Filesystem.deleteFile({
      path: this.concatenatePath(path, fileName),
      directory: this.cacheDirectory
    });
  }

  async deleteFolderIfExists(folderName: string): Promise<void> {
    let folderExists: boolean;

    try {
      await Filesystem.readdir({
        path: folderName,
        directory: Directory.Cache
      });
      folderExists = true;
    } catch {
      folderExists = false;
    }

    if (folderExists) {
      await Filesystem.rmdir({
        path: folderName,
        directory: Directory.Cache,
        recursive: true
      });
    }
  }

  async clearFileCache(): Promise<void> {
    for (const folder of Object.values(CACHE_FOLDERS)) {
      await this.deleteFolderIfExists(folder);
    }
  }

  concatenatePath(...pathFragments: string[]): string {
    if (!pathFragments || pathFragments.length === 0) {
      return undefined;
    }
    return pathFragments.reduce((path, currentPathFragment) => `${path}/${currentPathFragment}`);
  }

  markAsDownloaded(cacheDir: string, id: string): void {
    this.downloadedIconIds.add(`${cacheDir}-${id}`);
  }

  isDownloaded(cacheDir: string, id: string): boolean {
    return this.downloadedIconIds.has(`${cacheDir}-${id}`);
  }
}
